/**
 * InfinitSales - useAgentCheck Hook
 * Hook to check if user has agent configured and handle modal display
 * 
 * Usage:
 * ```jsx
 * const { 
 *   isAgentConfigured, 
 *   isChecking, 
 *   agentName,
 *   showCreateAgentModal,
 *   setShowCreateAgentModal,
 *   handleAgentError,
 *   CreateAgentModal 
 * } = useAgentCheck();
 * 
 * // In AI action handler:
 * try {
 *   await aiAgentService.translate(text, languages);
 * } catch (error) {
 *   if (handleAgentError(error)) return; // Modal will be shown
 *   // Handle other errors
 * }
 * 
 * // In render:
 * <CreateAgentModal />
 * ```
 */

import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Bot } from 'lucide-react';
import aiAgentService, { isAgentNotConfiguredError, getErrorRedirect } from '../../../services/global/aiAgentService';

export default function useAgentCheck() {
  const navigate = useNavigate();
  
  const [isChecking, setIsChecking] = useState(true);
  const [isAgentConfigured, setIsAgentConfigured] = useState(false);
  const [agentName, setAgentName] = useState('');
  const [showCreateAgentModal, setShowCreateAgentModal] = useState(false);
  const [redirectUrl, setRedirectUrl] = useState('/agent-onboarding');

  /**
   * Check agent status on mount
   */
  useEffect(() => {
    const checkStatus = async () => {
      setIsChecking(true);
      try {
        const status = await aiAgentService.checkStatus();
        setIsAgentConfigured(status.available);
        setAgentName(status.agent_name || '');
        if (status.redirect_if_no_agent) {
          setRedirectUrl(status.redirect_if_no_agent);
        }
      } catch (error) {
        console.error('[useAgentCheck] Status check failed:', error);
        setIsAgentConfigured(false);
      } finally {
        setIsChecking(false);
      }
    };

    checkStatus();
  }, []);

  /**
   * Handle errors from AI actions
   * Returns true if error was handled (modal shown), false otherwise
   */
  const handleAgentError = useCallback((error) => {
    if (isAgentNotConfiguredError(error)) {
      const redirect = getErrorRedirect(error);
      if (redirect) {
        setRedirectUrl(redirect);
      }
      setShowCreateAgentModal(true);
      return true;
    }
    return false;
  }, []);

  /**
   * Navigate to create agent page
   */
  const goToCreateAgent = useCallback(() => {
    setShowCreateAgentModal(false);
    navigate(redirectUrl);
  }, [navigate, redirectUrl]);

  /**
   * Modal component to show when agent not configured
   */
  const CreateAgentModal = useCallback(() => {
    if (!showCreateAgentModal) return null;

    return (
      <div style={modalStyles.overlay}>
        <div style={modalStyles.modal}>
          <div style={modalStyles.icon}>
            <Bot size={48} strokeWidth={1.6} />
          </div>
          <h2 style={modalStyles.title}>Configure seu Agente IA</h2>
          <p style={modalStyles.message}>
            Para usar o AI Workspace, você precisa criar e configurar seu agente de IA primeiro.
          </p>
          <p style={modalStyles.submessage}>
            O agente vai aprender sobre seu negócio e te ajudar a responder clientes de forma personalizada.
          </p>
          <div style={modalStyles.buttons}>
            <button 
              style={modalStyles.cancelBtn}
              onClick={() => setShowCreateAgentModal(false)}
            >
              Depois
            </button>
            <button 
              style={modalStyles.confirmBtn}
              onClick={goToCreateAgent}
            >
              Criar Agente
            </button>
          </div>
        </div>
      </div>
    );
  }, [showCreateAgentModal, goToCreateAgent]);

  return {
    isChecking,
    isAgentConfigured,
    agentName,
    showCreateAgentModal,
    setShowCreateAgentModal,
    handleAgentError,
    goToCreateAgent,
    redirectUrl,
    CreateAgentModal,
  };
}

// ============================================
// MODAL STYLES
// ============================================

const modalStyles = {
  overlay: {
    position: 'fixed',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: 'rgba(0, 0, 0, 0.5)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    zIndex: 9999,
  },
  modal: {
    backgroundColor: 'var(--bg-primary, #fff)',
    borderRadius: '16px',
    padding: '32px',
    maxWidth: '420px',
    width: '90%',
    textAlign: 'center',
    boxShadow: '0 20px 60px rgba(0, 0, 0, 0.3)',
  },
  icon: {
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    width: '80px',
    height: '80px',
    borderRadius: '50%',
    backgroundColor: 'var(--accent-primary-bg, rgba(34, 197, 94, 0.12))',
    color: 'var(--accent-primary, #22c55e)',
    marginBottom: '16px',
  },
  title: {
    fontSize: '20px',
    fontWeight: '600',
    color: 'var(--text-primary, #1a1a1a)',
    marginBottom: '12px',
    margin: '0 0 12px 0',
  },
  message: {
    fontSize: '15px',
    color: 'var(--text-secondary, #666)',
    marginBottom: '8px',
    lineHeight: 1.5,
    margin: '0 0 8px 0',
  },
  submessage: {
    fontSize: '13px',
    color: 'var(--text-tertiary, #999)',
    marginBottom: '24px',
    lineHeight: 1.5,
    margin: '0 0 24px 0',
  },
  buttons: {
    display: 'flex',
    gap: '12px',
    justifyContent: 'center',
  },
  cancelBtn: {
    padding: '12px 24px',
    borderRadius: '8px',
    border: '1px solid var(--border-color, #e0e0e0)',
    backgroundColor: 'transparent',
    color: 'var(--text-secondary, #666)',
    fontSize: '14px',
    fontWeight: '500',
    cursor: 'pointer',
    transition: 'all 0.2s',
  },
  confirmBtn: {
    padding: '12px 24px',
    borderRadius: '8px',
    border: 'none',
    backgroundColor: 'var(--accent-primary, #7c3aed)',
    color: '#fff',
    fontSize: '14px',
    fontWeight: '500',
    cursor: 'pointer',
    transition: 'all 0.2s',
  },
};